/**
 * Custom Node Component for Luma Skills
 *
 * Universal node component that adapts styling based on node type.
 * Future: Split into type-specific components for richer UIs.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { cn } from '@/lib/utils';
import {
  NodeType,
  getNodeColor,
  getNodeIcon,
  isTerminalNode
} from '@/types/lumaSkill';
import type { ReactFlowNodeData } from '@/utils/lumaSkillTransformers';
import * as Icons from 'lucide-react';

export const CustomNode = memo<NodeProps<ReactFlowNodeData>>(({ data, selected }) => {
  const { label, nodeType, properties } = data;

  const color = getNodeColor(nodeType);
  const iconName = getNodeIcon(nodeType);

  // Dynamically get icon component
  const IconComponent = (Icons as any)[iconName] || Icons.Circle;

  const isTerminal = isTerminalNode(nodeType);

  // Get node shape based on type
  const isDecision = nodeType === NodeType.DECISION_PROMPT || nodeType === NodeType.CONDITION;
  const isStart = nodeType === NodeType.START;
  const isEnd = nodeType === NodeType.END;

  // Extract preview text for some node types
  const getPreviewText = () => {
    if (properties.text) return properties.text;
    if (properties.prompt) return properties.prompt;
    if (properties.taskDescription) return properties.taskDescription;
    if (properties.attributeName) return properties.attributeName;
    if (properties.searchQuery) return properties.searchQuery;
    if (properties.responseTemplate) return properties.responseTemplate;
    return null;
  };

  const previewText = getPreviewText();

  return (
    <div
      className={cn(
        'min-w-[180px] max-w-[280px]',
        'border-2 transition-all',
        selected ? 'ring-2 ring-orange-500 ring-offset-2' : '',
        isDecision && 'rotate-45', // Diamond shape for decision nodes
        isStart && 'rounded-full',
        isEnd && 'rounded-full',
        !isStart && !isEnd && !isDecision && 'rounded-lg'
      )}
      style={{
        backgroundColor: color,
        borderColor: selected ? '#f97316' : adjustColor(color, -20)
      }}
    >
      {/* Input Handle (not for START) */}
      {nodeType !== NodeType.START && (
        <Handle
          type="target"
          position={isDecision ? Position.Left : Position.Top}
          className={cn(
            'w-3 h-3',
            isDecision && '-rotate-45' // Counter-rotate for diamond
          )}
        />
      )}

      {/* Node Content */}
      <div className={cn(
        'px-4 py-3',
        isDecision && '-rotate-45', // Counter-rotate content for diamond
        isStart && 'rounded-full',
        isEnd && 'rounded-full',
        !isStart && !isEnd && !isDecision && 'rounded-lg'
      )}>
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <IconComponent className="w-4 h-4 text-white flex-shrink-0" />
          <span className="font-medium text-white text-sm truncate">
            {label}
          </span>
        </div>

        {/* Preview Text */}
        {previewText && (
          <div className="text-xs text-white/80 line-clamp-2 mt-1">
            {previewText}
          </div>
        )}

        {/* Terminal indicator */}
        {isTerminal && (
          <div className="text-xs text-white/60 mt-1 font-mono">
            TERMINAL
          </div>
        )}
      </div>

      {/* Output Handle (not for terminal nodes) */}
      {!isTerminal && (
        <Handle
          type="source"
          position={isDecision ? Position.Right : Position.Bottom}
          className={cn(
            'w-3 h-3',
            isDecision && '-rotate-45' // Counter-rotate for diamond
          )}
        />
      )}
    </div>
  );
});

CustomNode.displayName = 'CustomNode';

/**
 * Adjust color brightness
 * @param color - Hex color string
 * @param amount - Amount to adjust (-100 to 100)
 */
function adjustColor(color: string, amount: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}
